import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";

const TIMING_LABELS = {
  avant_debut: "Avant le début",
  debut_travaux: "Début des travaux",
  pendant: "Pendant les travaux",
  fin_travaux: "Fin des travaux",
  apres_fin: "Après les travaux",
};

/** Calcule les jours calendaires entre aujourd'hui et une date cible */
function joursJusquA(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + "T00:00:00");
  const diff = Math.ceil((target - today) / 86400000);
  return diff;
}

/** Ajoute N jours à une date YYYY-MM-DD */
function addDays(dateStr, n) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

/** Calcule la date d'échéance d'une note BT selon le timing */
function getEcheanceBT(d, timing) {
  const debut = (d.dates_ce_mois || [])[0] || d.date_debut;
  const fin = (d.dates_ce_mois || []).at(-1) || d.date_fin;
  switch (timing) {
    case "avant_debut": return addDays(debut, -1);
    case "debut_travaux": return debut;
    case "pendant": return debut;
    case "fin_travaux": return fin;
    case "apres_fin": return addDays(fin, 1);
    default: return debut;
  }
}

/** Badge "Dans Xj" / "Aujourd'hui" / "Demain" / "En retard" */
function EcheanceBadge({ dateEcheance }) {
  if (!dateEcheance) return null;
  const jours = joursJusquA(dateEcheance);
  if (jours === null) return null;

  let label, color;
  if (jours < 0) {
    label = `En retard de ${Math.abs(jours)}j`;
    color = "bg-red-100 text-red-700 border border-red-200";
  } else if (jours === 0) {
    label = "Aujourd'hui !";
    color = "bg-orange-100 text-orange-700 border border-orange-200 font-bold";
  } else if (jours === 1) {
    label = "Demain";
    color = "bg-amber-100 text-amber-700 border border-amber-200 font-semibold";
  } else if (jours <= 5) {
    label = `Dans ${jours}j`;
    color = "bg-amber-50 text-amber-600 border border-amber-200";
  } else {
    label = `Dans ${jours}j`;
    color = "bg-slate-100 text-slate-500 border border-slate-200";
  }

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${color}`}>
      {label}
    </span>
  );
}

/**
 * Section dédiée aux notes importantes des BTs — TOUJOURS visible dans Vue d'ensemble.
 * Affiche les notes_importantes (avec timing/échéance) et notes_lavage_pression (avec date_lavage_prevu).
 * Triées par urgence (échéance la plus proche en premier).
 */
export function SectionNotesImportantes({ filteredDossiersProd, filteredDossiersProdEnriched, moisProd, yearProd, MONTHS }) {
  const source = filteredDossiersProdEnriched?.length > 0 ? filteredDossiersProdEnriched : filteredDossiersProd;

  // Construire une liste de notes avec leur échéance calculée
  const notes = [];
  for (const d of (source || [])) {
    if (d.notes_importantes) {
      const timing = d.notes_importantes_timing || "debut_travaux";
      const echeance = getEcheanceBT(d, timing);
      notes.push({
        key: `bt-${d.id}`,
        client: d.client_nom || d.nom_dossier,
        type: "bt",
        texte: d.notes_importantes,
        echeance,
        timingLabel: TIMING_LABELS[timing] || timing,
        jours: joursJusquA(echeance),
      });
    }
    if (d.notes_lavage_pression) {
      // Échéance = date de la route de lavage planifiée uniquement
      // Si pas encore cédulé → pas de countdown calculé
      const echeance = d.date_lavage_prevu || null;
      notes.push({
        key: `lavage-${d.id}`,
        client: d.client_nom || d.nom_dossier,
        type: "lavage",
        texte: d.notes_lavage_pression,
        echeance,
        timingLabel: d.date_lavage_prevu ? `Lavage prévu le ${d.date_lavage_prevu}` : "Lavage pas encore cédulé",
        jours: echeance ? joursJusquA(echeance) : null,
        pasCedule: !d.date_lavage_prevu,
      });
    }
  }

  // Trier par urgence (les plus proches en premier, en retard en tête)
  notes.sort((a, b) => {
    const ja = a.jours !== null ? a.jours : 9999;
    const jb = b.jours !== null ? b.jours : 9999;
    return ja - jb;
  });

  return (
    <Card className="border-amber-300 bg-amber-50/30" data-testid="section-notes-importantes">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          Notes importantes — {MONTHS?.[moisProd]} {yearProd}
          {notes.length > 0 && (
            <span className="text-xs font-normal text-amber-500 ml-1">
              ({notes.length} note{notes.length > 1 ? "s" : ""})
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-amber-600 mt-0.5">
          Notes BT ⚠ + Notes lavage à pression 🚿 — triées par échéance
        </p>
      </CardHeader>
      <CardContent>
        {notes.length === 0 ? (
          <p className="text-sm text-amber-400 italic">
            Aucune note importante pour {MONTHS?.[moisProd]} {yearProd}.
            Ajoutez une note dans le champ "Notes importantes" ou "Notes lavage à pression" d'un bon de travail.
          </p>
        ) : (
          <div className="space-y-2">
            {notes.map(note => (
              <div
                key={note.key}
                className={`bg-white border rounded-lg px-3 py-2.5 flex items-start gap-3 ${
                  note.jours !== null && note.jours < 0 ? "border-red-200 bg-red-50/30" :
                  note.jours !== null && note.jours <= 2 ? "border-amber-200 bg-amber-50/30" :
                  "border-slate-200"
                }`}
              >
                {/* Icône type */}
                <span className="text-base shrink-0 mt-0.5">
                  {note.type === "bt" ? "⚠" : "🚿"}
                </span>
                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-sm text-slate-800 truncate">{note.client}</span>
                    <span className="text-xs text-slate-400">{note.timingLabel}</span>
                  </div>
                  <p className={`text-sm ${note.type === "bt" ? "text-amber-900" : "text-blue-900"}`}>
                    {note.texte}
                  </p>
                </div>
                {/* Échéance */}
                <div className="shrink-0">
                  {note.pasCedule ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 border border-slate-200 italic">
                      Pas encore cédulé
                    </span>
                  ) : (
                    <EcheanceBadge dateEcheance={note.echeance} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
